import {
	getCookie as browserStorageGetCookie,
	setCookie as browserStorageSetCookie,
	Status as CacheWriteStatus,
	deleteCookie,
} from '@atlassian/browser-storage-controls';

import type { CachedState, InvitedAppShortcutsCache } from '../types';

import { getConfig } from './config';

const cacheKey = 'invitedAppShortcuts';
const localStorageCacheDebugKey = `${cacheKey}Debug`;

export { CacheWriteStatus };

type GetCookie = (key: string, ssr?: boolean) => string | undefined;

type SetCookie = (
	...[key, value, attributes]: Parameters<typeof browserStorageSetCookie>
) => Promise<SetCookieStringStatus | boolean | undefined>;

// Browser storage setCookie has a weak return type (string),
// but we're listing the statuses here that it actually returns:
type SetCookieStringStatus = 'SUCCESS' | 'BLOCKED' | 'FAILED' | string;

export class InviteAppShortcutsCookieCache implements InvitedAppShortcutsCache {
	#getCookie: GetCookie;
	#setCookie: SetCookie;

	constructor({
		getCookie = browserStorageGetCookie,
		setCookie = browserStorageSetCookie,
	}: {
		getCookie?: GetCookie;
		setCookie?: SetCookie;
	} = {}) {
		this.#getCookie = getCookie;
		this.#setCookie = setCookie;
	}

	async write(state: CachedState): Promise<boolean> {
		if (cacheDebugMode() === 'local-storage') {
			localStorage[cacheKey] = JSON.stringify(state);
			return true;
		}
		const result = await this.#setCookie(cacheKey, JSON.stringify(state), {
			expires: new Date(Date.now() + getConfig().CACHE_TIME_UNTIL_EXPIRY),
			sameSite: 'strict',
		});
		return result === true || result === CacheWriteStatus.SUCCESS;
	}

	read({ ssr }: { ssr: boolean }): CachedState | null {
		if (cacheDebugMode() === 'local-storage') {
			if (localStorage?.[cacheKey]) {
				return JSON.parse(localStorage[cacheKey]) as CachedState;
			}
		} else {
			const cookieValueString = this.#getCookie(cacheKey, ssr);
			if (cookieValueString) {
				return (JSON.parse(cookieValueString) as CachedState) || null;
			}
		}
		return null;
	}
}

const cacheDebugMode = () => {
	const debugMode =
		typeof localStorage !== 'undefined' &&
		localStorage &&
		// Extra safety while we're in the debug-only phase,
		// before we've switched to the proper browser controls (to be removed when that happens).
		// Requires manually setting localStorage.invitedAppShortcutsDebug = "true" in the console
		// to allow our code to write anything into the local storage cache.
		localStorage[localStorageCacheDebugKey];
	if (debugMode === 'cookie-delete') {
		localStorage[localStorageCacheDebugKey] = null;
		deleteCookie(cacheKey);
	} else if (debugMode && !['null', 'false'].includes(debugMode)) {
		return 'local-storage';
	}
	return null;
};
